"use strict";
/**
 * DEVICE-ORDER EMAIL PRESENTATION — the one copy.
 *
 * A device order becomes an email from TWO producers:
 *
 *   - hardware-service's `createOrder`
 *   - billing-service's `POST /onboarding/complete`, which writes the order row
 *     directly (ruling R5) and therefore bypasses the former entirely
 *
 * Both used to reduce the payload to `{skuId, quantity}` one statement before
 * the send, so a customer received, verbatim,
 * `1 × dev_mdl_cash_drawer_finance_60m_icia` with no price at all — while
 * `saleMode`, `amountCents` and `termMonths` sat on the order row untouched.
 *
 * Fixing that twice, in two repos with no compiler between them, is exactly how
 * one of the four order templates drifted from its three siblings. So the pure
 * part lives here: precedence, name and image extraction, the price wording and
 * the two-bucket money split. It performs NO I/O — each caller fetches its own
 * catalog rows and hands them in — which is what makes it unit-testable and
 * what makes both producers byte-identical by construction.
 */
Object.defineProperty(exports, "__esModule", { value: true });
exports.resolveDeviceOrderItemDevice = resolveDeviceOrderItemDevice;
exports.presentDeviceOrderItems = presentDeviceOrderItems;
exports.formatDeviceOrderMoney = formatDeviceOrderMoney;
exports.deviceOrderPriceLabel = deviceOrderPriceLabel;
exports.deviceOrderSubtotals = deviceOrderSubtotals;
exports.deviceOrderHasPricing = deviceOrderHasPricing;
exports.softwareGlyph = softwareGlyph;
exports.presentSoftwareLines = presentSoftwareLines;
exports.deviceOrderMoney = deviceOrderMoney;
exports.softwareSavingsCents = softwareSavingsCents;
exports.toHardwareCdnUrl = toHardwareCdnUrl;
/**
 * WHICH PRODUCT THIS LINE IS, in words a customer recognises.
 *
 * A BUNDLE wins over a MODEL when an item names both — the precedence
 * `resolveDevice` uses in offerPresentation.ts, so a kit is described as the kit
 * the customer bought rather than as one component of it.
 *
 * Presence of the bundle row decides, not how much the row happens to carry: a
 * bare bundle falling back to one of its components would confidently name the
 * WRONG product, which is worse than naming none.
 *
 * Returns `{}` rather than a fabricated name when neither row is found.
 */
function resolveDeviceOrderItemDevice(item, bundles = [], models = []) {
    if (!item)
        return {};
    const bundle = item.bundleId ? bundles.find((b) => b?.bundleId === item.bundleId) : undefined;
    if (bundle) {
        const out = {};
        if (bundle.name)
            out.displayName = bundle.name;
        if (bundle.heroImageUrl)
            out.imageUrl = bundle.heroImageUrl;
        return out;
    }
    const model = item.modelId ? models.find((m) => m?.modelId === item.modelId) : undefined;
    if (model) {
        const out = {};
        if (model.modelName)
            out.displayName = model.modelName;
        if (model.media?.heroImageUrl)
            out.imageUrl = model.media.heroImageUrl;
        return out;
    }
    return {};
}
/**
 * The order's items AS THE EMAIL NEEDS THEM.
 *
 * Pricing is copied through untouched — it is authoritative and is never
 * recomputed here. Only the name and the image are resolved.
 *
 * Built key by key rather than spread, deliberately: an order row also carries
 * cost, margin and audit fields, and a spread would put them one template
 * mistake away from a customer's inbox. A key is emitted only when it has a
 * value, so a payload from an older producer stays exactly `{skuId, quantity}`.
 */
function presentDeviceOrderItems(items, { bundles = [], models = [], cdnDomain } = {}) {
    if (!Array.isArray(items))
        return [];
    return items.filter(Boolean).map((item) => {
        const device = resolveDeviceOrderItemDevice(item, bundles, models);
        const out = { skuId: item.skuId, quantity: item.quantity };
        if (device.displayName)
            out.displayName = device.displayName;
        if (device.imageUrl)
            out.imageUrl = toHardwareCdnUrl(device.imageUrl, cdnDomain);
        if (item.saleMode)
            out.saleMode = item.saleMode;
        if (typeof item.amountCents === 'number')
            out.amountCents = item.amountCents;
        if (typeof item.termMonths === 'number')
            out.termMonths = item.termMonths;
        return out;
    });
}
/** cents -> real money with thousands grouping, e.g. 129900 -> "$1,299.00". */
function formatDeviceOrderMoney(amountCents) {
    const [whole, frac] = (Math.abs(amountCents) / 100).toFixed(2).split('.');
    return `${amountCents < 0 ? '-' : ''}$${whole.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}.${frac}`;
}
/**
 * HOW THIS LINE IS PAID, worded the way the customer agreed to it.
 *
 * Returns '' when the producer sent no pricing, or a sale mode this does not
 * recognise. An omitted price row is honest; a fabricated "$0.00" is not, and
 * guessing at an unknown mode is how a wrong number reaches an inbox.
 */
function deviceOrderPriceLabel(item) {
    const mode = item?.saleMode;
    if (!mode)
        return '';
    if (mode === 'included')
        return 'Included';
    if (typeof item.amountCents !== 'number')
        return '';
    const amount = formatDeviceOrderMoney(item.amountCents);
    if (mode === 'installment')
        return item.termMonths ? `${amount}/mo × ${item.termMonths} months` : `${amount}/mo`;
    if (mode === 'rental' || mode === 'lease')
        return `${amount}/mo rental`;
    if (mode === 'outright')
        return `${amount} once`;
    return '';
}
/**
 * TWO BUCKETS, NEVER ONE.
 *
 * The house rule stated in super-admin-ui's bundleFinance.ts: "The upfront money
 * and the monthly money are different kinds of money and are never added
 * together." A single total mixing a $990 outright charge with a $45/mo payment
 * is precisely the figure a customer would quote back at you in a dispute, so
 * this returns two numbers and there is deliberately no helper that sums them.
 *
 * `amountCents` is per unit, so a line costs `amountCents × quantity` — the
 * convention every other surface uses (OrderDetail.tsx, PaymentVerifyPanel.tsx,
 * bundleFinance.ts). An unpriced line is skipped, never counted as zero.
 */
function deviceOrderSubtotals(items) {
    let dueTodayCents = 0;
    let monthlyCents = 0;
    for (const item of Array.isArray(items) ? items : []) {
        if (!item?.saleMode || item.saleMode === 'included')
            continue;
        if (typeof item.amountCents !== 'number')
            continue;
        const lineCents = item.amountCents * (item.quantity || 1);
        if (item.saleMode === 'outright')
            dueTodayCents += lineCents;
        else if (item.saleMode === 'installment' || item.saleMode === 'rental' || item.saleMode === 'lease') {
            monthlyCents += lineCents;
        }
    }
    return { dueTodayCents, monthlyCents };
}
/** True once a producer is sending pricing at all — i.e. any line has a sale mode. */
function deviceOrderHasPricing(items) {
    return (Array.isArray(items) ? items : []).some((i) => !!i?.saleMode);
}
/**
 * THE PACKAGE'S FACE, IN AN EMAIL.
 *
 * super-admin stores a LUCIDE ICON NAME, which every screen renders as an SVG
 * React component. Neither of those survives here: Gmail strips inline `<svg>`
 * outright, and a React component has no meaning in a string of HTML. Hosting
 * a PNG per icon would work but there is no such asset set and no bucket
 * fronting one — see `toHardwareCdnUrl` for how that ends.
 *
 * So the ICON degrades to a unicode glyph and the COLOUR is kept exactly: the
 * tile a customer sees in their inbox is the same colour as the card they
 * clicked in the wizard, which is the half of the identity that actually
 * carries recognition. The map is keyed by the Lucide names super-admin
 * actually offers; an unmapped one falls through to the name patterns below,
 * and then to a generic box — an unmatched package must read as deliberate,
 * never as broken.
 */
const GLYPH_BY_LUCIDE_ICON = {
    ShieldCheck: '🛡', Shield: '🛡', Users: '👥', User: '👥', Boxes: '📦',
    Package: '📦', Layers: '🗂', CreditCard: '💳', Sparkles: '✨', Megaphone: '📣',
    Gift: '🎁', Globe: '🌐', Truck: '🚚', Brain: '🧠', BarChart3: '📊',
    CalendarDays: '📅', Calendar: '📅', Monitor: '🖥', ShoppingBag: '🛍',
    Receipt: '🧾', Coins: '🪙', Tag: '🏷', Store: '🏬', Wrench: '🔧',
    Settings: '⚙', Zap: '⚡', Lock: '🔒', Bot: '🤖', Camera: '📷', Mail: '✉',
};
/**
 * The same name patterns platform-app's `PACKAGE_ICONS` uses, in the same
 * order, so a package with no stored icon gets the SAME face in the inbox as
 * it has on the wizard card. Kept in step by hand; there is no compiler
 * between a React icon table and a string of email HTML.
 */
const GLYPH_BY_NAME = [
    [/complian|age.?verif|regulat/i, '🛡'],
    [/team|staff|schedul|payroll|employee/i, '👥'],
    [/vault|invent|stock|supplier|purchas/i, '📦'],
    [/catalog|product|barcode/i, '🗂'],
    [/till|checkout|register|tender/i, '💳'],
    [/creator|studio|design|media/i, '✨'],
    [/marketing|promo|campaign|advertis/i, '📣'],
    [/loyal|reward|customer/i, '🎁'],
    [/online|storefront|ecommerce|web/i, '🌐'],
    [/deliver|fulfil|shipping|transfer/i, '🚚'],
    [/\bai\b|intelligen|agent|insight/i, '🧠'],
    [/report|analytic|dashboard/i, '📊'],
    [/rota|calendar|booking|appointment/i, '📅'],
    [/pos|terminal|device|kiosk/i, '🖥'],
];
/** The package's stored icon when it has one; the name-matched guess otherwise. */
function softwareGlyph(name, icon) {
    if (icon && GLYPH_BY_LUCIDE_ICON[icon])
        return GLYPH_BY_LUCIDE_ICON[icon];
    for (const [pattern, glyph] of GLYPH_BY_NAME)
        if (pattern.test(name || ''))
            return glyph;
    return '📦';
}
/**
 * The software lines AS THE EMAIL NEEDS THEM.
 *
 * Prices are copied through untouched — they are what the customer was quoted
 * and are never recomputed here. A list price is emitted ONLY when it is
 * strictly above the settled price: equal means no discount, and lower means
 * stale catalogue data, and neither is something to strike through and call a
 * saving.
 */
function presentSoftwareLines(lines, brands = []) {
    const byId = new Map((brands || []).filter((b) => b?.id).map((b) => [b.id, b]));
    return (Array.isArray(lines) ? lines : [])
        .filter((l) => l && (l.kind === 'plan' || l.kind === 'package'))
        .map((l) => {
        const brand = l.packageId ? byId.get(l.packageId) : undefined;
        const displayName = l.name || brand?.name || l.skuId;
        const quantity = Math.max(1, l.quantity || 1);
        const unit = typeof l.amountCents === 'number' ? l.amountCents : 0;
        const out = {
            skuId: l.skuId,
            displayName,
            kind: l.kind === 'plan' ? 'plan' : 'package',
            quantity,
            monthlyCents: unit * quantity,
            glyph: softwareGlyph(displayName, brand?.icon),
        };
        const list = typeof l.listAmountCents === 'number' ? l.listAmountCents * quantity : undefined;
        if (typeof list === 'number' && list > out.monthlyCents)
            out.listMonthlyCents = list;
        // `introAmountCents: 0` is a real promotion — free for N months — so the
        // test is on the TYPE, and the months have to be a real period for the
        // phase to mean anything.
        if (typeof l.introAmountCents === 'number' && (l.introMonths || 0) >= 1) {
            out.introMonthlyCents = l.introAmountCents * quantity;
            out.introMonths = Math.round(l.introMonths);
        }
        const accent = brand?.color || brand?.gradientFrom;
        if (accent)
            out.accentColor = accent;
        return out;
    });
}
function deviceOrderMoney(args) {
    // Hardware keeps its own two-bucket split, unchanged — `dueTodayCents` there
    // is outright purchases and `monthlyCents` is device rent and installments.
    const hardware = deviceOrderSubtotals(args.items);
    // Non-hardware one-offs (deposits, setup fees) are a SEPARATE list: a deposit
    // is not on the device order at all, it is a billing line beside it.
    let extraOneTime = 0;
    for (const l of Array.isArray(args.oneTime) ? args.oneTime : []) {
        if (!l || typeof l.amountCents !== 'number')
            continue;
        extraOneTime += l.amountCents * Math.max(1, l.quantity || 1);
    }
    let softwareMonthly = 0;
    let softwareFirstMonth = 0;
    let introMonths = 0;
    for (const l of Array.isArray(args.software) ? args.software : []) {
        if (!l)
            continue;
        softwareMonthly += l.monthlyCents;
        // Again: `introMonthlyCents` of 0 is a real free month.
        softwareFirstMonth += typeof l.introMonthlyCents === 'number' ? l.introMonthlyCents : l.monthlyCents;
        if (typeof l.introMonthlyCents === 'number' && (l.introMonths || 0) > introMonths) {
            introMonths = l.introMonths;
        }
    }
    const oneTimeCents = hardware.dueTodayCents + extraOneTime;
    // Device rent is not promotable — only software carries intro phases — so it
    // sits in both the first cycle and the ongoing rate at the same amount.
    const firstMonthCents = softwareFirstMonth + hardware.monthlyCents;
    const monthlyCents = softwareMonthly + hardware.monthlyCents;
    const out = {
        oneTimeCents,
        firstMonthCents,
        monthlyCents,
        dueTodayCents: oneTimeCents + firstMonthCents,
        hasIntro: firstMonthCents !== monthlyCents,
    };
    if (out.hasIntro && introMonths >= 1)
        out.introMonths = introMonths;
    return out;
}
/**
 * Total saved against list, over the lines we can actually price at list.
 *
 * `null` when NOTHING has a known list price: "you saved $0" and "we cannot
 * tell you what you saved" are different sentences, and only one of them is
 * true. Never negative — a settled price above list is stale data, not a
 * surcharge to announce.
 */
function softwareSavingsCents(lines) {
    let saving = 0;
    let priced = 0;
    for (const l of Array.isArray(lines) ? lines : []) {
        if (!l || typeof l.listMonthlyCents !== 'number')
            continue;
        priced += 1;
        saving += Math.max(0, l.listMonthlyCents - l.monthlyCents);
    }
    return priced === 0 ? null : saving;
}
const S3_HOST_STYLE = /^https?:\/\/[\w.-]+\.s3(?:[.-][\w-]+)?\.amazonaws\.com\//i;
const S3_PATH_STYLE = /^https?:\/\/s3(?:[.-][\w-]+)?\.amazonaws\.com\/[\w.-]+\//i;
/**
 * Put a hardware hero image on the CDN instead of exposing the bucket.
 *
 * A NO-OP WHEN `cdnDomain` IS EMPTY, AND THAT IS THE STATE TODAY. There is no
 * CloudFront distribution in front of `agenticpos-hardware-media-{stage}`: the
 * existing `image.{dev.}rubytill.com` distributions front `agenticpos-media-*`,
 * a different bucket, and serving a hardware key through them returns 403
 * (verified). Rewriting onto them would break every product photo in every
 * customer's inbox, which is strictly worse than a working S3 URL that merely
 * names the bucket.
 *
 * So the transform is wired and gated on config. The day infrastructure puts a
 * distribution in front of that bucket, setting HARDWARE_MEDIA_CDN_DOMAIN
 * switches every email over with no code change.
 *
 * Only OUR S3 URLs are rewritten. A vendor's own image URL is not ours to
 * repoint, and a URL already on the CDN is left alone.
 */
function toHardwareCdnUrl(url, cdnDomain) {
    if (!url)
        return '';
    if (!cdnDomain)
        return url;
    if (url.includes(`//${cdnDomain}/`))
        return url;
    if (S3_HOST_STYLE.test(url))
        return `https://${cdnDomain}/${url.replace(S3_HOST_STYLE, '')}`;
    if (S3_PATH_STYLE.test(url))
        return `https://${cdnDomain}/${url.replace(S3_PATH_STYLE, '')}`;
    return url;
}
//# sourceMappingURL=deviceOrderPresentation.js.map